import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import marketingCrewService from '../services/marketingCrewService';

/**
 * Marketing Crew Dashboard
 * 
 * Comprehensive AI agent workflow management for:
 * - Market Research
 * - Content Production
 * - Campaign Optimization
 * - Audience Analysis
 * - Behavioral Analytics
 */

const WORKFLOW_TYPES = [
  {
    id: 'market-research',
    name: 'Market Research',
    icon: '🔍',
    description: 'Comprehensive market analysis, competitor research, and trend identification',
    color: '#3b82f6'
  },
  {
    id: 'content-production',
    name: 'Content Production',
    icon: '✍️',
    description: 'AI-driven content creation, optimization, and multi-platform adaptation',
    color: '#10b981'
  },
  {
    id: 'campaign-optimization',
    name: 'Campaign Optimization',
    icon: '📈',
    description: 'Performance analysis, A/B testing recommendations, and budget optimization',
    color: '#f59e0b'
  }
];

const AUDIENCE_ANALYSIS_TYPES = [
  { id: 'demographics', name: 'Demographics Analysis', icon: '👥' },
  { id: 'psychographics', name: 'Psychographic Profile', icon: '🧠' },
  { id: 'personas', name: 'Customer Personas', icon: '🎭' },
  { id: 'journey-map', name: 'Journey Mapping', icon: '🗺️' },
  { id: 'comprehensive', name: 'Comprehensive Report', icon: '📊' }
];

const BEHAVIORAL_ANALYSIS_TYPES = [
  { id: 'purchase-patterns', name: 'Purchase Patterns', icon: '🛒' },
  { id: 'engagement', name: 'Engagement Analysis', icon: '💬' },
  { id: 'decision-profiling', name: 'Decision Profiling', icon: '🎯' },
  { id: 'segmentation', name: 'Customer Segmentation', icon: '📦' },
  { id: 'predictions', name: 'Behavior Predictions', icon: '🔮' }
];

const STATUS_COLORS = {
  pending: '#6b7280',
  running: '#3b82f6',
  completed: '#10b981',
  failed: '#ef4444',
  cancelled: '#9ca3af'
};

const MarketingCrew = () => {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState('workflows');
  const [agents, setAgents] = useState([]);
  const [crewStatus, setCrewStatus] = useState(null);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedWorkflowType, setSelectedWorkflowType] = useState(null);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState(null);
  
  // Form state
  const [workflowConfig, setWorkflowConfig] = useState({
    target_market: '',
    competitors: '',
    objectives: '',
    budget: '',
    timeline: '7',
    platforms: [],
    content_types: []
  });

  const [analysisConfig, setAnalysisConfig] = useState({
    customer_data: '',
    time_range: '30',
    segments: '',
    goals: ''
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsData, statusData, historyData] = await Promise.all([
        marketingCrewService.getAgents().catch(() => ({ agents: [] })),
        marketingCrewService.getStatus().catch(() => null),
        marketingCrewService.getWorkflowHistory().catch(() => ({ workflows: [] }))
      ]);
      
      setAgents(agentsData.agents || []);
      setCrewStatus(statusData);
      setWorkflowHistory(historyData.workflows || []);
    } catch (err) {
      console.error('Error fetching crew data:', err);
      setError('Failed to load marketing crew data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Start workflow
  const handleStartWorkflow = async () => {
    if (!selectedWorkflowType) return;
    
    setLoading(true);
    try {
      let result;
      switch (selectedWorkflowType.id) {
        case 'market-research':
          result = await marketingCrewService.executeMarketResearch(workflowConfig);
          break;
        case 'content-production':
          result = await marketingCrewService.executeContentProduction(workflowConfig);
          break;
        case 'campaign-optimization':
          result = await marketingCrewService.executeCampaignOptimization(workflowConfig);
          break;
        default:
          throw new Error('Unknown workflow type');
      }
      
      setShowWorkflowModal(false);
      setSelectedWorkflow(result);
      fetchData();
    } catch (err) {
      console.error('Error starting workflow:', err);
      setError('Failed to start workflow');
    } finally {
      setLoading(false);
    }
  };

  // Run analysis
  const handleRunAnalysis = async () => {
    if (!selectedAnalysisType) return;
    
    setLoading(true);
    try {
      let result;
      switch (selectedAnalysisType.id) {
        case 'demographics':
          result = await marketingCrewService.analyzeDemographics(analysisConfig);
          break;
        case 'psychographics':
          result = await marketingCrewService.createPsychographicProfile(analysisConfig);
          break;
        case 'personas':
          result = await marketingCrewService.generatePersonas(analysisConfig);
          break;
        case 'journey-map':
          result = await marketingCrewService.mapCustomerJourney(analysisConfig);
          break;
        case 'comprehensive':
          result = await marketingCrewService.generateAudienceReport(analysisConfig);
          break;
        case 'purchase-patterns':
          result = await marketingCrewService.analyzePurchasePatterns(analysisConfig);
          break;
        case 'engagement':
          result = await marketingCrewService.analyzeEngagement(analysisConfig);
          break;
        case 'decision-profiling':
          result = await marketingCrewService.profileDecisionMaking(analysisConfig);
          break;
        case 'segmentation':
          result = await marketingCrewService.segmentCustomers(analysisConfig);
          break;
        case 'predictions':
          result = await marketingCrewService.predictBehavior(analysisConfig);
          break;
        default:
          throw new Error('Unknown analysis type');
      }
      
      setShowAnalysisModal(false);
      setSelectedWorkflow(result);
    } catch (err) {
      console.error('Error running analysis:', err);
      setError('Failed to run analysis');
    } finally {
      setLoading(false);
    }
  };

  // Render agent card
  const renderAgentCard = (agent) => (
    <div key={agent.id} className="agent-card">
      <div className="agent-header">
        <div className="agent-info">
          <span className="agent-icon">{agent.icon || '🤖'}</span>
          <div>
            <h4 className="agent-name">{agent.name}</h4>
            <span className="agent-role">{agent.role}</span>
          </div>
        </div>
        <div className={`badge ${agent.status === 'active' ? 'success' : 'secondary'}`}>
          {agent.status?.toUpperCase() || 'IDLE'}
        </div>
      </div>
      <p className="agent-description">{agent.description}</p>
      <div className="agent-capabilities">
        {agent.capabilities?.map((cap, idx) => (
          <span key={idx} className="capability-badge">
            {cap}
          </span>
        ))}
      </div>
    </div>
  );

  // Render workflow card
  const renderWorkflowCard = (workflow) => (
    <div 
      key={workflow.id} 
      className="workflow-card"
      onClick={() => setSelectedWorkflow(workflow)}
      style={{ 
        borderLeft: `4px solid ${STATUS_COLORS[workflow.status] || '#6b7280'}`,
        background: selectedWorkflow?.id === workflow.id ? 'var(--primary-50)' : 'var(--bg-secondary)'
      }}
    >
      <div className="workflow-header">
        <div>
          <h4 className="workflow-name">{workflow.name}</h4>
          <span className="workflow-type">{workflow.type}</span>
        </div>
        <div 
          className="badge"
          style={{ 
            background: STATUS_COLORS[workflow.status],
            color: 'white'
          }}
        >
          {workflow.status?.toUpperCase()}
        </div>
      </div>
      <div className="workflow-meta">
        Started: {new Date(workflow.created_at).toLocaleString()}
      </div>
      {workflow.progress !== undefined && (
        <div className="progress-bar mb-2">
          <div 
            className="progress-fill"
            style={{ 
              width: `${workflow.progress}%`,
              background: STATUS_COLORS[workflow.status]
            }}
          />
        </div>
      )}
      {workflow.results_summary && (
        <p className="workflow-summary">{workflow.results_summary}</p>
      )}
    </div>
  );

  // Render workflow modal
  const renderWorkflowModal = () => (
    <div className="modal-overlay" onClick={() => setShowWorkflowModal(false)}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {selectedWorkflowType?.icon} Start {selectedWorkflowType?.name} Workflow
          </h3>
          <button className="btn-icon" onClick={() => setShowWorkflowModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <p className="text-secondary mb-4">{selectedWorkflowType?.description}</p>
          
          <div className="form-group">
            <label>Target Market / Industry</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., E-commerce, SaaS, Healthcare"
              value={workflowConfig.target_market}
              onChange={e => setWorkflowConfig({...workflowConfig, target_market: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Key Competitors (comma-separated)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., Company A, Company B, Company C"
              value={workflowConfig.competitors}
              onChange={e => setWorkflowConfig({...workflowConfig, competitors: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Primary Objectives</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Describe your main goals for this workflow..."
              value={workflowConfig.objectives}
              onChange={e => setWorkflowConfig({...workflowConfig, objectives: e.target.value})}
            />
          </div>

          <div className="grid-2-col gap-4">
            <div className="form-group">
              <label>Budget ($)</label>
              <input
                type="number"
                className="form-control"
                placeholder="Monthly budget"
                value={workflowConfig.budget}
                onChange={e => setWorkflowConfig({...workflowConfig, budget: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Timeline (days)</label>
              <select
                className="form-control"
                value={workflowConfig.timeline}
                onChange={e => setWorkflowConfig({...workflowConfig, timeline: e.target.value})}
              >
                <option value="7">1 Week</option>
                <option value="14">2 Weeks</option>
                <option value="30">1 Month</option>
                <option value="90">3 Months</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Target Platforms</label>
            <div className="flex gap-2 flex-wrap">
              {['Facebook', 'Instagram', 'LinkedIn', 'Twitter', 'TikTok', 'YouTube', 'Google'].map(platform => (
                <label key={platform} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={workflowConfig.platforms.includes(platform)}
                    onChange={e => {
                      if (e.target.checked) {
                        setWorkflowConfig({...workflowConfig, platforms: [...workflowConfig.platforms, platform]});
                      } else {
                        setWorkflowConfig({...workflowConfig, platforms: workflowConfig.platforms.filter(p => p !== platform)});
                      }
                    }}
                  />
                  {platform}
                </label>
              ))}
            </div>
          </div>

          {selectedWorkflowType?.id === 'content-production' && (
            <div className="form-group">
              <label>Content Types</label>
              <div className="flex gap-2 flex-wrap">
                {['Blog Posts', 'Social Media', 'Email', 'Video Scripts', 'Ad Copy', 'Landing Pages'].map(type => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={workflowConfig.content_types.includes(type)}
                      onChange={e => {
                        if (e.target.checked) {
                          setWorkflowConfig({...workflowConfig, content_types: [...workflowConfig.content_types, type]});
                        } else {
                          setWorkflowConfig({...workflowConfig, content_types: workflowConfig.content_types.filter(t => t !== type)});
                        }
                      }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowWorkflowModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleStartWorkflow}
            disabled={!workflowConfig.target_market || !workflowConfig.objectives}
          >
            🚀 Start Workflow
          </button>
        </div>
      </div>
    </div>
  );

  // Render analysis modal
  const renderAnalysisModal = () => (
    <div className="modal-overlay" onClick={() => setShowAnalysisModal(false)}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {selectedAnalysisType?.icon} {selectedAnalysisType?.name}
          </h3>
          <button className="btn-icon" onClick={() => setShowAnalysisModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Customer Data Source</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Describe your customer data or paste a sample..."
              value={analysisConfig.customer_data}
              onChange={e => setAnalysisConfig({...analysisConfig, customer_data: e.target.value})}
            />
          </div>

          <div className="grid-2-col gap-4">
            <div className="form-group">
              <label>Time Range (days)</label>
              <select
                className="form-control"
                value={analysisConfig.time_range}
                onChange={e => setAnalysisConfig({...analysisConfig, time_range: e.target.value})}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
                <option value="all">All time</option>
              </select>
            </div>
            <div className="form-group">
              <label>Customer Segments</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., New, Returning, VIP"
                value={analysisConfig.segments}
                onChange={e => setAnalysisConfig({...analysisConfig, segments: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Analysis Goals</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="What insights are you looking for?"
              value={analysisConfig.goals}
              onChange={e => setAnalysisConfig({...analysisConfig, goals: e.target.value})}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowAnalysisModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleRunAnalysis}
            disabled={!analysisConfig.customer_data}
          >
            🔬 Run Analysis
          </button>
        </div>
      </div>
    </div>
  );

  // Render workflow results
  const renderWorkflowResults = () => {
    if (!selectedWorkflow) return null;

    return (
      <div className="card">
        <div className="flex justify-between align-center mb-4">
          <h3 className="card-title mb-0">
            Workflow Results: {selectedWorkflow.name}
          </h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedWorkflow(null)}>
            ✕ Close
          </button>
        </div>

        <div className="stats-grid-4 mb-4">
          <div className="stat-item">
            <div className="stat-value">{selectedWorkflow.status}</div>
            <div className="stat-label">Status</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{selectedWorkflow.progress || 0}%</div>
            <div className="stat-label">Progress</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{selectedWorkflow.tasks_completed || 0}</div>
            <div className="stat-label">Tasks Done</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{selectedWorkflow.duration || '-'}</div>
            <div className="stat-label">Duration</div>
          </div>
        </div>

        {selectedWorkflow.results && (
          <div className="results-container">
            <h4>Results</h4>
            <pre className="code-block">
              {JSON.stringify(selectedWorkflow.results, null, 2)}
            </pre>
          </div>
        )}

        {selectedWorkflow.recommendations && (
          <div className="recommendations mt-4">
            <h4>AI Recommendations</h4>
            <div className="grid-1-col gap-2">
              {selectedWorkflow.recommendations.map((rec, idx) => (
                <div key={idx} className="card-sm" style={{ background: 'var(--success-50)' }}>
                  <div className="flex gap-2">
                    <span>💡</span>
                    <div>
                      <strong>{rec.title}</strong>
                      <p className="text-sm text-secondary mb-0">{rec.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && !agents.length) {
    return (
      <div>
        <div className="loading-spinner">Loading Marketing Crew...</div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Marketing Crew</span>
      </nav>

      <div className="card">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1>🤖 Marketing Crew</h1>
            <p className="text-secondary">AI-powered multi-agent workflows for marketing automation</p>
          </div>
          {crewStatus && (
            <div className={`badge ${crewStatus.active ? 'success' : 'secondary'}`} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
              {crewStatus.active ? '🟢 Crew Active' : '⚪ Crew Idle'}
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            {error}
            <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs mb-4">
          {['workflows', 'agents', 'audience', 'behavioral'].map(tab => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'workflows' && '🚀 Workflows'}
              {tab === 'agents' && '🤖 Agents'}
              {tab === 'audience' && '👥 Audience Analysis'}
              {tab === 'behavioral' && '📊 Behavioral Analytics'}
            </button>
          ))}
        </div>

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <div className="grid-2-col gap-4">
            {/* Workflow Types */}
            <div className="inner-card">
              <h3 className="card-title">Start New Workflow</h3>
              <div className="grid-1-col gap-3">
                {WORKFLOW_TYPES.map(workflow => (
                  <div
                    key={workflow.id}
                    className="workflow-type-card"
                    style={{ borderLeft: `4px solid ${workflow.color}` }}
                    onClick={() => {
                      setSelectedWorkflowType(workflow);
                      setShowWorkflowModal(true);
                    }}
                  >
                    <div className="flex align-center gap-3">
                      <span style={{ fontSize: '2rem' }}>{workflow.icon}</span>
                      <div>
                        <h4 className="workflow-type-name">{workflow.name}</h4>
                        <p className="workflow-type-desc">{workflow.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          {/* Workflow History */}
          <div className="inner-card">
            <h3 className="card-title">Recent Workflows</h3>
            {workflowHistory.length > 0 ? (
              <div className="grid-1-col gap-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {workflowHistory.map(renderWorkflowCard)}
              </div>
            ) : (
              <div className="empty-state">
                <span style={{ fontSize: '3rem' }}>📋</span>
                <p>No workflows yet. Start your first workflow!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && (
        <div className="inner-card">
          <h3 className="card-title">Available AI Agents</h3>
          <div className="grid-3-col gap-4">
            {agents.length > 0 ? agents.map(renderAgentCard) : (
              <>
                {/* Default agents if none loaded */}
                {[
                  { id: 1, name: 'Market Researcher', icon: '🔍', role: 'Research Specialist', status: 'active', description: 'Analyzes market trends, competitors, and opportunities', capabilities: ['Competitor Analysis', 'Trend Detection', 'SWOT Analysis'] },
                  { id: 2, name: 'Content Creator', icon: '✍️', role: 'Content Specialist', status: 'active', description: 'Creates compelling content across all formats', capabilities: ['Copywriting', 'SEO', 'Multi-platform'] },
                  { id: 3, name: 'Campaign Strategist', icon: '📈', role: 'Strategy Lead', status: 'active', description: 'Develops and optimizes campaign strategies', capabilities: ['Budget Optimization', 'A/B Testing', 'ROI Analysis'] },
                  { id: 4, name: 'Data Analyst', icon: '📊', role: 'Analytics Expert', status: 'active', description: 'Processes and interprets marketing data', capabilities: ['Data Visualization', 'Predictive Analytics', 'Reporting'] },
                  { id: 5, name: 'Social Media Manager', icon: '📱', role: 'Social Specialist', status: 'idle', description: 'Manages social media presence and engagement', capabilities: ['Scheduling', 'Engagement', 'Community Management'] },
                  { id: 6, name: 'SEO Optimizer', icon: '🎯', role: 'SEO Specialist', status: 'active', description: 'Improves search visibility and rankings', capabilities: ['Keyword Research', 'Technical SEO', 'Link Building'] }
                ].map(renderAgentCard)}
              </>
            )}
          </div>
        </div>
      )}

      {/* Audience Analysis Tab */}
      {activeTab === 'audience' && (
        <div className="inner-card">
          <h3 className="card-title">Audience Analysis Tools</h3>
          <p className="text-secondary mb-4">Deep dive into your customer demographics, psychographics, and behavior patterns</p>
          <div className="grid-3-col gap-4">
            {AUDIENCE_ANALYSIS_TYPES.map(analysis => (
              <div
                key={analysis.id}
                className="analysis-card"
                onClick={() => {
                  setSelectedAnalysisType(analysis);
                  setShowAnalysisModal(true);
                }}
              >
                <span className="analysis-icon">{analysis.icon}</span>
                <h4 className="analysis-name">{analysis.name}</h4>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Behavioral Analytics Tab */}
      {activeTab === 'behavioral' && (
        <div className="inner-card">
          <h3 className="card-title">Behavioral Analytics</h3>
          <p className="text-secondary mb-4">Understand and predict customer behavior patterns</p>
          <div className="grid-3-col gap-4">
            {BEHAVIORAL_ANALYSIS_TYPES.map(analysis => (
              <div
                key={analysis.id}
                className="analysis-card"
                onClick={() => {
                  setSelectedAnalysisType(analysis);
                  setShowAnalysisModal(true);
                }}
              >
                <span className="analysis-icon">{analysis.icon}</span>
                <h4 className="analysis-name">{analysis.name}</h4>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workflow Results */}
      {selectedWorkflow && renderWorkflowResults()}

      {/* Modals */}
      {showWorkflowModal && renderWorkflowModal()}
      {showAnalysisModal && renderAnalysisModal()}
      
      <style>{`
        /* Agent Card */
        .agent-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1rem;
          transition: all 0.2s ease;
        }
        .agent-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .agent-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        .agent-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .agent-icon {
          font-size: 1.5rem;
        }
        .agent-name {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }
        .agent-role {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }
        .agent-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin: 0 0 0.75rem 0;
        }
        .agent-capabilities {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .capability-badge {
          font-size: 0.7rem;
          padding: 0.25rem 0.5rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 9999px;
          color: var(--text-secondary);
        }
        
        /* Workflow Card */
        .workflow-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .workflow-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .workflow-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }
        .workflow-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 0.25rem 0;
        }
        .workflow-type {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }
        .workflow-meta {
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }
        .workflow-summary {
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin: 0;
        }
        
        /* Analysis Card */
        .analysis-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .analysis-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .analysis-icon {
          font-size: 3rem;
          display: block;
          margin-bottom: 1rem;
        }
        .analysis-name {
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-primary);
          margin: 0;
        }
        
        /* Workflow Type Card */
        .workflow-type-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .workflow-type-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .workflow-type-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 0.25rem 0;
        }
        .workflow-type-desc {
          font-size: 0.8rem;
          color: var(--text-secondary);
          margin: 0;
        }
        
        /* Progress Bar */
        .progress-bar {
          height: 6px;
          background: var(--bg-tertiary);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          transition: width 0.3s;
        }
        
        /* Misc */
        .hover-lift {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--bg-secondary);
          border-radius: var(--radius-sm);
          cursor: pointer;
          font-size: 0.875rem;
        }
        .checkbox-label:hover {
          background: var(--bg-tertiary);
        }
        .code-block {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: var(--radius-sm);
          overflow-x: auto;
          font-size: 0.8rem;
          max-height: 300px;
          overflow-y: auto;
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
      </div>
    </div>
  );
};

export default MarketingCrew;
